import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Check, Download, ImageOff, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { errMsg, imageUrl } from "@/lib/api";

const PAGE = 24;

// Grid of past AI images. onPick(item) -> reuse; selectedId highlights the attached one.
export default function AiGalleryGrid({ onPick, pickLabel = "Guna", selectedId, allowDelete = false }) {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (skip = 0) => {
    setBusy(true);
    try {
      const { data } = await api.get("/ai/images", { params: { skip, limit: PAGE } });
      setItems((prev) => (skip ? [...(prev || []), ...data.items] : data.items));
      setTotal(data.total);
    } catch (e) { toast.error(errMsg(e)); setItems((p) => p || []); } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(0); }, [load]);

  const remove = async (it) => {
    if (!window.confirm(`Padam gambar "${it.title}"?`)) return;
    try {
      await api.delete(`/ai/images/${it.id}`);
      setItems((p) => p.filter((x) => x.id !== it.id));
      setTotal((t) => t - 1);
      toast.success("Gambar dipadam");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (items === null) return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
  if (items.length === 0) return (
    <div className="panel flex flex-col items-center gap-2 p-10 text-center text-sm text-muted-foreground" data-testid="ai-gallery-empty">
      <ImageOff className="h-8 w-8" /> Belum ada gambar AI. Jana gambar pertama di Tulis Post.
    </div>
  );

  return (
    <div data-testid="ai-gallery-grid">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((it, i) => (
          <div key={it.id} className={`group fade-up overflow-hidden rounded-lg border bg-card ${selectedId === it.id ? "border-violet-500 ring-1 ring-violet-500" : "border-border"}`} style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }} data-testid={`ai-gallery-item-${it.id}`}>
            <button type="button" onClick={() => onPick(it)} className="relative block w-full" data-testid={`ai-gallery-pick-${it.id}`}>
              <img src={imageUrl(it.id)} alt={it.title} loading="lazy" className="aspect-square w-full object-cover transition-transform duration-200 group-hover:scale-105" />
              {selectedId === it.id && <span className="absolute right-2 top-2 rounded-full bg-violet-600 p-1 text-white"><Check className="h-3.5 w-3.5" /></span>}
            </button>
            <div className="space-y-2 p-2.5">
              <p className="truncate text-sm font-medium" title={it.title}>{it.title}</p>
              <p className="font-mono text-[10px] text-muted-foreground">{format(new Date(it.created_at), "d/M/yyyy h:mm a")}</p>
              <div className="flex items-center gap-1">
                <Button size="sm" className="h-7 flex-1 text-xs" onClick={() => onPick(it)} data-testid={`ai-gallery-use-${it.id}`}>{pickLabel}</Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" asChild data-testid={`ai-gallery-download-${it.id}`}><a href={imageUrl(it.id)} download={`${it.title}.png`}><Download className="h-3.5 w-3.5" /></a></Button>
                {allowDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-300 hover:text-red-200" onClick={() => remove(it)} data-testid={`ai-gallery-delete-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
          </div>
        ))}
      </div>
      {items.length < total && (
        <div className="mt-4 flex justify-center">
          <Button variant="secondary" onClick={() => load(items.length)} disabled={busy} data-testid="ai-gallery-more-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Lagi ({total - items.length})
          </Button>
        </div>
      )}
    </div>
  );
}
